export type LayoutMode = "mobile" | "tablet" | "desktop";
export type LayoutOrientation = "portrait" | "landscape";

export const MOBILE_BREAKPOINT_PX = 768;
export const TABLET_BREAKPOINT_PX = 1024;

export function detectMode(viewportWidth: number): LayoutMode {
  if (viewportWidth < MOBILE_BREAKPOINT_PX) return "mobile";
  if (viewportWidth < TABLET_BREAKPOINT_PX) return "tablet";
  return "desktop";
}

export function detectOrientation(
  viewportWidth: number,
  viewportHeight: number,
): LayoutOrientation {
  return viewportHeight >= viewportWidth ? "portrait" : "landscape";
}

export type LayoutChangeCallback<TConfig> = (config: TConfig) => void;

/**
 * Observer base class. Subclasses define their own `TConfig` shape and the
 * `compute()` logic that builds it from window state plus any subclass-managed
 * mutable inputs (e.g. game scene flag, viewer rotation). `equals()` defaults
 * to "never equal" — preserving the always-notify behavior that some consumers
 * relied on before this extraction; subclasses override for change-detection.
 */
export abstract class BaseLayoutManager<TConfig> {
  protected config: TConfig;
  private readonly listeners = new Set<LayoutChangeCallback<TConfig>>();

  constructor() {
    this.config = this.compute();
    window.addEventListener("resize", this.onResize);
    window.addEventListener("orientationchange", this.onResize);
  }

  getConfig(): TConfig {
    return this.config;
  }

  onChange(cb: LayoutChangeCallback<TConfig>): () => void {
    this.listeners.add(cb);
    return () => {
      this.listeners.delete(cb);
    };
  }

  /** Recompute from current inputs and notify listeners if anything changed. */
  refresh(): void {
    const next = this.compute();
    if (this.equals(next, this.config)) return;
    this.config = next;
    for (const cb of this.listeners) cb(next);
  }

  dispose(): void {
    window.removeEventListener("resize", this.onResize);
    window.removeEventListener("orientationchange", this.onResize);
    this.listeners.clear();
  }

  protected abstract compute(): TConfig;

  /** Default: always notify. Subclasses override for field-by-field equality. */
  protected equals(_a: TConfig, _b: TConfig): boolean {
    return false;
  }

  private readonly onResize = (): void => {
    this.refresh();
  };
}
